import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Trash2, ChevronDown, Check, Pencil, Plus, MapPin, X, GripVertical, Paperclip, Search } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { numberToWords } from "./ctcWords";

export const CURRENCIES = [
    { code: "INR", label: "₹ INR" },
    { code: "USD", label: "$ USD" },
    { code: "EUR", label: "€ EUR" },
    { code: "GBP", label: "£ GBP" },
    { code: "AED", label: "AED" },
    { code: "SGD", label: "SGD" },
    { code: "AUD", label: "AUD" },
    { code: "CAD", label: "CAD" },
];

export const QUESTION_TYPES = [
    { value: "text", label: "Short text" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
    { value: "boolean", label: "Yes / No" },
    { value: "select", label: "Dropdown" },
    { value: "multiselect", label: "Checkboxes" },
    { value: "file", label: "File upload" },
];

// Definitive types can be scored; free `text` is always informational (never scored).
const DEFINITIVE_TYPES = new Set(["number", "date", "boolean", "select", "multiselect"]);

// Short type label for the collapsed summary chip.
const TYPE_LABEL = Object.fromEntries(QUESTION_TYPES.map(t => [t.value, t.label]));
export const QUESTION_TYPE_LABEL = TYPE_LABEL;

export const slugify = (label) =>
    (label || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || `q_${Date.now()}`;

// Match-mode options for number/date ideal answers.
export const MATCH_MODES = [
    { value: "exact", label: "exactly" },
    { value: "max", label: "at most" },
    { value: "min", label: "at least" },
];

// Compact symbols for the collapsed scoring summary.
const MATCH_SYMBOLS = { exact: "=", max: "≤", min: "≥" };

// Predefined questions (today's defaults) grouped into categories for the library palette.
// `short` is the dense palette label; `label` is the full question text shown in the editor.
export const QUESTION_LIBRARY = [
    {
        category: "Interest",
        questions: [
            { key: "interested", short: "Interested", label: "Are you interested in exploring this opportunity?", type: "boolean", required: true },
        ],
    },
    {
        category: "Documents",
        questions: [
            { key: "resume_upload", short: "Resume", label: "Please upload your CV/resume", type: "file", required: false },
            { key: "has_passport", short: "Passport", label: "Do you have a valid passport?", type: "boolean", required: false },
        ],
    },
    {
        category: "Experience",
        questions: [
            { key: "total_experience_years", short: "Total experience", label: "How many years of total work experience do you have?", type: "number", required: false, match_mode: "min" },
        ],
    },
    {
        category: "Compensation",
        questions: [
            { key: "expected_ctc", short: "Expected CTC", label: "What is your expected CTC?", type: "number", required: false },
            { key: "current_ctc", short: "Current CTC", label: "What is your current CTC?", type: "number", required: false },
        ],
    },
    {
        category: "Availability",
        questions: [
            // Notice period doubles as the availability gate — the candidate page collects an
            // earliest-join date inline (only when they pick "immediate") and it is scored
            // against this same ceiling, so there is no separate "available from" question.
            { key: "notice_period_days", short: "Notice period", label: "What is your notice period (in days)?", type: "number", required: true },
            { key: "call_availability", short: "Call availability", label: "When are you available for a quick call?", type: "multiselect", required: false, options: ["Weekday mornings", "Weekday afternoons", "Weekday evenings", "Weekends"] },
        ],
    },
    {
        category: "Location & work mode",
        questions: [
            { key: "work_preference", short: "Work mode", label: "What is your preferred work mode?", type: "multiselect", required: false, options: ["Remote", "Hybrid", "On-site"] },
            { key: "open_to_locations", short: "Open to locations", label: "Which of these locations are you open to working from?", type: "multiselect", required: false, options: [] },
            { key: "willing_to_relocate", short: "Open to relocate", label: "Are you willing to relocate for this role?", type: "boolean", required: false },
        ],
    },
];

// Question keys whose options are sourced from the form's job locations, not edited inline.
export const LOCATION_QUESTION_KEY = "open_to_locations";
// Canonical remote option injected into location questions.
export const REMOTE_OPTION = "Remote / WFH";

// CTC question keys — currency is shared between them.
const CTC_KEYS = ["expected_ctc", "current_ctc"];

// Library gates whose direction is fixed to "at most" (a ceiling) — no mode selector.
const MAX_LOCKED_KEYS = new Set(["expected_ctc", "notice_period_days"]);

// Keys that must never become a scoring gate, regardless of what ideal_answer a stale
// or legacy question object carries — the UI hides their gate row, so this makes sure a
// re-save actually clears it instead of silently round-tripping it.
const NEVER_GATED_KEYS = new Set(["current_ctc", "call_availability"]);

// Stacks a caption (and optional hint) above a control. Deliberately a plain function rather
// than a component: a component declared inside the render would be a new type every pass, so
// React would remount the wrapped input and steal focus on every keystroke.
const labelledField = (label, node, { hint, required } = {}) => (
    <div className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium text-gray-700 dark:text-gray-300">
            {label}
            {required && <span className="text-red-500 dark:text-red-400"> *</span>}
        </span>
        {node}
        {hint && <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">{hint}</p>}
    </div>
);

// Helper: short currency label for the collapsed chip (e.g. "INR").
const currencyLabel = (code) => {
    const found = CURRENCIES.find(c => c.code === code);
    return found ? found.code : (code || "");
};

// Flat list of every library question and the set of library keys (fixed-type questions).
const LIBRARY_FLAT = QUESTION_LIBRARY.flatMap(c => c.questions);
const LIBRARY_KEYS = new Set(LIBRARY_FLAT.map(q => q.key));

// Stable flat index for each library question — needed for Draggable index props.
const LIBRARY_KEY_INDEX = Object.fromEntries(LIBRARY_FLAT.map((q, i) => [q.key, i]));

// A fully-formed active question from a bare library/custom definition.
const toActiveQuestion = (q) => ({
    key: q.key || slugify(q.label),
    label: q.label,
    type: q.type || "text",
    required: !!q.required,
    options: q.options || [],
    ideal_answer: q.key === "interested" ? true : q.type === "multiselect" ? [] : "",
    match_mode: MAX_LOCKED_KEYS.has(q.key) ? "max" : (q.match_mode || "exact"),
});

// Seed for a brand-new screening form: only the "interested" question.
export const defaultActiveQuestions = () =>
    LIBRARY_FLAT.filter(q => q.key === "interested").map(toActiveQuestion);

// A non-empty ideal answer. `false` is a valid boolean preference; a non-empty array
// is a valid multiselect preference; only null/blank-string/empty-array count as unset.
const isIdealSet = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === "string") return v.trim() !== "";
    if (Array.isArray(v)) return v.length > 0;
    return true;
};

// Options as a clean string array (select/multiselect may store a raw comma string mid-edit).
const parseOptions = (opts) =>
    (Array.isArray(opts) ? opts : String(opts || "").split(",")).map(o => String(o).trim()).filter(Boolean);

// A question is scored iff it's a definitive type with a set ideal answer.
export const isScored = (q) => DEFINITIVE_TYPES.has(q.type) && isIdealSet(q.ideal_answer);

// One-line summary of the preferred answer for the collapsed row (null when not scored).
export const scoreSummary = (q) => {
    if (!isScored(q)) return null;
    const v = q.ideal_answer;
    if (q.type === "boolean") return (v === true || v === "true" || v === "yes") ? "Yes" : "No";
    if (q.type === "select" || q.type === "multiselect") {
        const arr = Array.isArray(v) ? v : [v];
        return arr.length <= 1 ? String(arr[0] ?? "") : `${arr[0]} +${arr.length - 1}`;
    }
    return `${MATCH_SYMBOLS[q.match_mode || "exact"]} ${v}`; // number / date
};

// Normalises raw questions into the API shape (fills keys, parses select/multiselect
// options, attaches scoring fields only for definitive types with an ideal answer, and
// forces `required` on any scored question — text is always informational).
export const cleanQuestions = (questions) =>
    (questions || [])
        .filter(q => q.label?.trim())
        .map(q => {
            const hasOptions = q.type === "select" || q.type === "multiselect";
            const out = {
                key: q.key?.trim() || slugify(q.label),
                label: q.label.trim(),
                type: q.type,
                required: !!q.required,
                ...(hasOptions ? { options: parseOptions(q.options) } : {}),
            };
            // open_to_locations is always a gate once it offers cities: any overlap with the
            // offered locations qualifies (offering a location means it's acceptable).
            if (out.key === LOCATION_QUESTION_KEY && Array.isArray(out.options) && out.options.length) {
                out.ideal_answer = out.options;
                out.required = true;
                return out;
            }
            // "interested" has no UI control to change its qualifying answer — always Yes.
            if (out.key === "interested") {
                out.ideal_answer = true;
                return out;
            }
            // A gate = definitive type with a set ideal answer; text stays informational.
            // Gating is decoupled from required: a gate no longer forces the question required
            // (out.required keeps the recruiter's choice). An optional gate left blank scores
            // neutral; only a non-matching answer disqualifies (see screening_scorer.py).
            // NEVER_GATED_KEYS never attach a gate here even if the incoming question object
            // already carries a stale ideal_answer (legacy data) — the UI has no control to set
            // or clear one for these keys, so a re-save must actually drop it.
            if (!NEVER_GATED_KEYS.has(out.key) && DEFINITIVE_TYPES.has(q.type) && isIdealSet(q.ideal_answer)) {
                let ideal = q.ideal_answer;
                if (q.type === "boolean") ideal = (ideal === true || ideal === "true" || ideal === "yes");
                else if (q.type === "multiselect") ideal = (Array.isArray(ideal) ? ideal : [ideal]).map(v => String(v).trim()).filter(Boolean);
                else ideal = String(ideal).trim();
                out.ideal_answer = ideal;
                if (q.type === "number" || q.type === "date") out.match_mode = MAX_LOCKED_KEYS.has(out.key) ? "max" : (q.match_mode || "exact");
            }
            return out;
        });


export const firstMissingOptions = (cleaned) =>
    cleaned.find(q => q.type === "multiselect" && (!q.options || q.options.length === 0)) || null;

// First question (raw, pre-clean) with blank text — catches an empty custom question
// before cleanQuestions would silently drop it.
export const firstEmptyLabel = (questions) =>
    (questions || []).find(q => !q.label?.trim()) || null;

// Small status pill used in the collapsed summary row. `size="md"` is opt-in (used by the wizard
// variant's Knock-Out chip) — the default stays byte-identical to before for every other caller.
// `tone="gradient"` is the filled pink→blue pill (wizard Knock-Out), matching the wizard's
// primary button and library add-check.
const Chip = ({ children, tone = "muted", size = "sm" }) => {
    // Dark tints are translucent white/colour over the page rather than the light theme's
    // solid pastels — the same treatment the sequence and review steps use, so a chip reads
    // as a chip on either background instead of a bright block of colour.
    const tones = {
        type: "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300",
        score: "bg-purple-50 text-purple-600 border border-purple-100 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30",
        req: "bg-blue-50 text-blue-600 border border-blue-100 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30",
        must: "bg-amber-50 text-amber-600 border border-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/40",
        lock: "bg-gray-800 text-white dark:bg-white/15 dark:text-white",
        muted: "bg-gray-50 text-gray-400 border border-gray-100 dark:bg-white/5 dark:text-gray-400 dark:border-gray-700/50",
    };
    const sizes = {
        sm: "px-1.5 py-0.5 text-[10px]",
        md: "px-2 py-1 text-[11px]",
    };
    if (tone === "gradient") {
        // A true gradient *border* over a barely-tinted fill: three background layers — tint
        // and an opaque fill clipped to the padding box, the full pink→blue gradient clipped
        // to the border box — since border-image can't do rounded corners. `.knockout-tile`
        // carries all of that; `.knockout-chip` restates the fill for dark, where the shared
        // rule's translucent layer would let the gradient flood the pill and swallow the
        // label. Sized to exactly 20px tall (18px leading + 1px borders) to match the text-sm
        // line box it sits in.
        return (
            <span className="knockout-tile knockout-chip inline-flex items-center rounded-full font-medium whitespace-nowrap px-2.5 text-[11px] leading-[18px]">
                <span className="knockout-tile-text">{children}</span>
            </span>
        );
    }
    return (
        <span className={`rounded font-medium whitespace-nowrap ${sizes[size]} ${tones[tone]}`}>
            {children}
        </span>
    );
};

// Compact combobox used in place of native <select> — a browser's native option list can't be
// restyled (no control over the gap to the trigger, option padding, or hover/selected colors),
// so every dropdown in this builder (type, currency, match mode, qualifying-answer) uses this
// instead, matching the app's standard dropdown look (pill trigger, mt-2 gap, shadowed list).
const Dropdown = ({ value, options, onChange, disabled, placeholder = "Select…", title, widthClass = "" }) => {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const ref = useRef(null);

    // The list is portaled to <body> (fixed-positioned off the trigger's rect) rather than
    // absolutely positioned in place, because several question-row cards clip overflow for their
    // rounded corners — an in-flow dropdown panel gets cut off by that ancestor's overflow-hidden.
    useEffect(() => {
        if (!open) return undefined;
        // Capture-phase, so a scroll in any ancestor container (the modal body, the page)
        // closes the panel rather than leaving it parked at a stale rect. That reaches the
        // panel's own scroll too, though — which would shut the list the moment you tried
        // to reach the options below the fold — so those are let through.
        const close = (e) => {
            if (e?.target?.closest?.("[data-dropdown-list]")) return;
            setOpen(false);
        };
        const onOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target) && !e.target.closest("[data-dropdown-list]")) setOpen(false);
        };
        document.addEventListener("mousedown", onOutside);
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        return () => {
            document.removeEventListener("mousedown", onOutside);
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
        };
    }, [open]);

    const toggle = () => {
        if (!open && ref.current) setRect(ref.current.getBoundingClientRect());
        setOpen(o => !o);
    };

    const selected = options.find(o => o.value === value);

    return (
        <div ref={ref} className={`relative ${widthClass}`}>
            <button
                type="button"
                disabled={disabled}
                title={title}
                onClick={toggle}
                className={`w-full flex items-center justify-between gap-2 text-xs border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-transparent px-3 py-1.5 outline-none transition-colors ${
                    disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-gray-400 dark:hover:border-gray-500"
                }`}
            >
                <span className="truncate text-gray-700 dark:text-[#CCCCCC]">{selected ? selected.label : placeholder}</span>
                {!disabled && (
                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                )}
            </button>
            {open && rect && createPortal(
                <ul
                    data-dropdown-list
                    style={{ position: "fixed", top: rect.bottom + 8, left: rect.left, minWidth: rect.width }}
                    // Portaled to <body>, which sits outside the themed page wrapper — so the
                    // panel names an opaque dark surface itself rather than inheriting one.
                    className="max-w-[220px] border border-gray-300 dark:border-gray-700 rounded-md shadow-lg bg-white dark:bg-[#1B1524] z-[9999] max-h-52 overflow-y-auto py-1"
                >
                    {options.map(o => (
                        <li
                            key={o.value}
                            onClick={() => { onChange(o.value); setOpen(false); }}
                            className={`px-3 py-2 text-xs cursor-pointer transition-colors whitespace-nowrap ${
                                o.value === value
                                    ? "bg-gray-100 text-gray-900 font-medium dark:bg-white/10 dark:text-white"
                                    : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
                            }`}
                        >
                            {o.label}
                        </li>
                    ))}
                </ul>,
                document.body
            )}
        </div>
    );
};

/* Chip editor for a select/multiselect question's answer options.
 *
 * Replaces a plain text box bound to `options.join(", ")`. That round-tripped the array
 * through a string on every keystroke — split(",") kept the space after each comma, and the
 * next join re-added one, so the gaps grew as you typed and a backspace over one was undone
 * on the same keystroke. Options are a list, so they are edited as a list: the committed
 * values are chips and only the word being typed lives in the text box.
 */
const OptionsTagInput = ({ options, onChange, compact = false, placeholder }) => {
    const [draft, setDraft] = useState("");

    const commit = (raw) => {
        // Split on commas so a pasted "Remote, Hybrid, On-site" lands as three options.
        const added = raw.split(",").map(s => s.trim()).filter(Boolean);
        setDraft("");
        if (!added.length) return;
        const next = [...options];
        added.forEach(a => {
            if (!next.some(o => o.toLowerCase() === a.toLowerCase())) next.push(a);
        });
        if (next.length !== options.length) onChange(next);
    };

    const removeAt = (i) => onChange(options.filter((_, idx) => idx !== i));

    return (
        <div
            className={`flex items-center flex-wrap gap-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-transparent focus-within:border-purple-400 ${
                compact ? "flex-1 min-w-[160px] px-2 py-1" : "w-full px-3 py-2"
            }`}
        >
            {options.map((o, i) => (
                <span
                    key={`${o}-${i}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 text-purple-600 text-xs border border-purple-100 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30 shrink-0"
                >
                    {o}
                    <button type="button" onClick={() => removeAt(i)} className="hover:text-purple-800 dark:hover:text-purple-200" title={`Remove ${o}`}>
                        <X className="w-3 h-3" />
                    </button>
                </span>
            ))}
            <input
                type="text"
                value={draft}
                onChange={e => {
                    // The separator commits rather than being typed, so a comma never sits
                    // in the draft waiting to be re-parsed.
                    if (e.target.value.includes(",")) commit(e.target.value);
                    else setDraft(e.target.value);
                }}
                onKeyDown={e => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        commit(draft);
                    } else if (e.key === "Backspace" && !draft && options.length) {
                        // Backspace on an empty box takes back the last chip — the gesture
                        // every tag field has, and the one the old input couldn't honour.
                        removeAt(options.length - 1);
                    }
                }}
                // Going straight from typing to "Add question" must not discard the word
                // in hand: the button's click blurs this field first.
                onBlur={() => commit(draft)}
                placeholder={options.length === 0 ? placeholder : "Add another"}
                className={`flex-1 min-w-[90px] outline-none bg-transparent text-textSecondary dark:text-[#CCCCCC] ${compact ? "text-xs py-0.5" : "text-sm py-0.5"}`}
            />
        </div>
    );
};

// Read-only question row for previews elsewhere (e.g. sequence creator) — same chips as
// the builder's collapsed summary row, minus drag/expand/delete affordances.
export const QuestionSummaryRow = ({ q, currency }) => {
    const scored = isScored(q);
    const isLocationQ = q.key === LOCATION_QUESTION_KEY;
    const qOptions = parseOptions(q.options);
    const hasRemote = isLocationQ && qOptions.some(l => l === REMOTE_OPTION);
    const cities = qOptions.filter(l => l !== REMOTE_OPTION);
    const isCTC = CTC_KEYS.includes(q.key);
    const summary = scoreSummary(q);
    return (
        <div className="flex items-center gap-2 px-3 py-2.5">
            <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-white truncate">
                {q.label || <span className="text-gray-400 dark:text-gray-500 italic font-normal">Untitled question</span>}
            </span>
            <Chip tone="type">{TYPE_LABEL[q.type]}</Chip>
            {isCTC && currency && <Chip tone="muted">{currencyLabel(currency)}</Chip>}
            {isLocationQ && cities.length > 0 && <Chip tone="muted">{cities.length} {cities.length === 1 ? "city" : "cities"}</Chip>}
            {isLocationQ && hasRemote && <Chip tone="muted">Remote</Chip>}
            {isLocationQ ? (qOptions.length > 0 ? null : <Chip tone="muted">Info</Chip>)
                : scored ? <Chip tone="score">{summary}</Chip>
                : q.required ? <Chip tone="req">Required</Chip>
                : <Chip tone="muted">Info</Chip>}
        </div>
    );
};

/**
 * Screening question editor — slim library palette (left, ~1/3) + wide collapsible
 * question rows (right, ~2/3). Library items are draggable into the questions panel
 * to insert at a specific position, or clickable to append at the bottom. Questions
 * can be reordered by dragging the grip handle. The "interested" question is always
 * locked to position 0 and cannot be reordered.
 * Props: questions (array), onChange (fn(newArray)), currency, onCurrencyChange.
 */
const QuestionsBuilder = ({ questions, onChange, currency, onCurrencyChange, showLibrarySearch = false, variant = "classic" }) => {
    // Keys of questions whose editor is expanded (newly added ones auto-open).
    const [openKeys, setOpenKeys] = useState(() => new Set());
    const [locationInput, setLocationInput] = useState("");
    // Opt-in library filter (default off keeps existing callers unchanged).
    const [librarySearch, setLibrarySearch] = useState("");
    // Wizard-variant quick-add text (Yes/No / Descriptive pills) — unused by the classic variant.
    const [customLabel, setCustomLabel] = useState("");
    // Tracks which question keys have already received the one-time auto-focus so that
    // typing in any field doesn't re-fire the focus and steal the cursor.
    const focusedOnce = useRef(new Set());

    const activeKeys = new Set(questions.map(q => q.key));

    // Which CTC question "owns" the currency selector is derived per-question inside
    // renderEditorFields, so an unsaved draft can own it too.

    // Library palette filtered by the optional search box (matches short label, full text, or category).
    const librarySearchTerm = librarySearch.trim().toLowerCase();
    const filteredLibrary = !librarySearchTerm
        ? QUESTION_LIBRARY
        : QUESTION_LIBRARY
              .map(group => ({
                  ...group,
                  questions: group.questions.filter(q =>
                      (q.short || "").toLowerCase().includes(librarySearchTerm) ||
                      (q.label || "").toLowerCase().includes(librarySearchTerm) ||
                      group.category.toLowerCase().includes(librarySearchTerm)
                  ),
              }))
              .filter(group => group.questions.length > 0);

    const update = (idx, field, value) =>
        onChange(questions.map((q, i) => (i === idx ? { ...q, [field]: value } : q)));
    const removeAt = (idx) => onChange(questions.filter((_, i) => i !== idx));
    const removeByKey = (key) => onChange(questions.filter(q => q.key !== key));

    const openOne = (key) => setOpenKeys(prev => new Set(prev).add(key));
    const toggleOpen = (key) => setOpenKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const toggleLibrary = (libQ) => {
        if (activeKeys.has(libQ.key)) removeByKey(libQ.key);
        else { onChange(pinLocked([...questions, toActiveQuestion(libQ)])); openOne(libQ.key); }
    };

    // A blank custom question, edited in place in the wide editor.
    const addCustom = () => {
        const key = `custom_${Date.now()}`;
        onChange([...questions, toActiveQuestion({ key, label: "", type: "text" })]);
        openOne(key);
    };

    // ── Wizard configure-in-a-modal flow ────────────────────────────────────────
    // Adding a question (from the library, by drop, or custom) and editing one both route
    // through the same modal, so a question is only ever committed to `questions` once it has
    // been configured. Shape: { mode, draft, index, insertAt }. `insertAt` preserves the drop
    // position for library→questions drags; null appends.
    const [modal, setModal] = useState(null);

    const openAddModal = (base, insertAt = null) =>
        setModal({ mode: "add", draft: toActiveQuestion(base), index: null, insertAt });
    const openEditModal = (q, index) =>
        setModal({ mode: "edit", draft: { ...q }, index, insertAt: null });
    const openCustomModal = () =>
        openAddModal({ key: `custom_${Date.now()}`, label: "", type: "text" });

    const closeModal = () => { setModal(null); setLocationInput(""); };
    const patchDraft = (field, value) =>
        setModal(m => (m ? { ...m, draft: { ...m.draft, [field]: value } } : m));

    /* Why the draft can't be added yet, as a sentence — or null when it can.
     *
     * Stated as a reason rather than a boolean so the disabled button explains itself here,
     * while the question is in front of you. These are the same checks the wizard's step gate
     * applies, deliberately: the location question used to be exempt from the options rule
     * here but not there, so it could be added with no cities and then block Configure
     * Outreach from a screen that gave no hint which question was at fault. */
    const draftBlocker = (() => {
        if (!modal) return null;
        const d = modal.draft;
        if (!d.label.trim()) return "Give the question some text.";
        if ((d.type === "select" || d.type === "multiselect") && parseOptions(d.options).length === 0) {
            return d.key === LOCATION_QUESTION_KEY
                ? "Add at least one job location — the candidate picks which of these they are open to."
                : "Add at least one answer option — these are the choices the candidate picks from.";
        }
        return null;
    })();
    const draftReady = !!modal && !draftBlocker;

    const saveModal = () => {
        if (!draftReady) return;
        const d = modal.draft;
        if (modal.mode === "edit") {
            onChange(questions.map((q, i) => (i === modal.index ? d : q)));
        } else {
            const locked = questions.filter(q => POSITION_LOCKED_KEYS.includes(q.key));
            const rest = questions.filter(q => !POSITION_LOCKED_KEYS.includes(q.key));
            if (modal.insertAt == null) rest.push(d);
            else rest.splice(modal.insertAt, 0, d);
            onChange(pinLocked([...locked, ...rest]));
        }
        closeModal();
    };

    // Wizard-variant quick-add: creates a custom question with a preset type from the
    // "Type a question…" row's Yes/No / Descriptive pills.
    const addCustomWithType = (type) => {
        const label = customLabel.trim();
        if (!label) return;
        const key = `custom_${Date.now()}`;
        onChange([...questions, toActiveQuestion({ key, label, type })]);
        openOne(key);
        setCustomLabel("");
    };

    // Location helpers write through an `onField` setter rather than a question index, so the
    // same editor fields drive both the inline (classic) editor and the wizard's modal draft.
    const addLocation = (q, onField) => {
        const c = locationInput.trim();
        if (!c) return;
        const current = parseOptions(q.options);
        if (!current.some(l => l.toLowerCase() === c.toLowerCase())) {
            onField("options", [...current, c]);
        }
        setLocationInput("");
    };

    const removeLocation = (q, onField, city) => {
        onField("options", parseOptions(q.options).filter(l => l !== city));
    };

    // Position-locked questions: never draggable, always rendered first in this fixed order
    // ("interested" can't be removed at all; "resume_upload" is optional but when present is
    // always pinned right after it — see the dedicated document card below). Given any array,
    // pinLocked pulls these to the front in order and leaves everything else's relative order
    // untouched — every mutation site below routes its result through it.
    const POSITION_LOCKED_KEYS = ["interested", "resume_upload"];
    const pinLocked = (arr) => {
        const locked = POSITION_LOCKED_KEYS.map(key => arr.find(q => q.key === key)).filter(Boolean);
        return [...locked, ...arr.filter(q => !POSITION_LOCKED_KEYS.includes(q.key))];
    };

    // Drag-and-drop: library → questions (insert at position) or questions → questions (reorder).
    const handleDragEnd = ({ source, destination, draggableId }) => {
        if (!destination) return;
        const nonLocked = questions.filter(q => !POSITION_LOCKED_KEYS.includes(q.key));

        if (source.droppableId === "library" && destination.droppableId === "questions") {
            if (activeKeys.has(draggableId)) return; // already in form — no-op
            const libQ = LIBRARY_FLAT.find(q => q.key === draggableId);
            if (!libQ) return;
            const next = [...nonLocked];
            next.splice(destination.index, 0, toActiveQuestion(libQ));
            onChange(pinLocked([...questions.filter(q => POSITION_LOCKED_KEYS.includes(q.key)), ...next]));
            openOne(draggableId);
        } else if (source.droppableId === "questions" && destination.droppableId === "questions") {
            if (source.index === destination.index) return;
            const next = [...nonLocked];
            const [moved] = next.splice(source.index, 1);
            next.splice(destination.index, 0, moved);
            onChange(pinLocked([...questions.filter(q => POSITION_LOCKED_KEYS.includes(q.key)), ...next]));
        }
    };

    // Split questions into the position-locked cards (always first, in fixed order) and the
    // sortable rest.
    const interestedQ = questions.find(q => q.key === "interested");
    const resumeQ = questions.find(q => q.key === "resume_upload");
    const sortableQuestions = questions.filter(q => !POSITION_LOCKED_KEYS.includes(q.key));

    // Renders the expanded editor body for a question (shared between locked and sortable cards).
    // `hideLabelInput` skips the boxed label field when the caller already made the label
    // inline-editable in its own header row (the wizard row does this to save vertical space).
    // `showLabels` stacks a caption (and where useful a hint) above each control — on for the
    // modal, where the fields stand alone and need to explain themselves; off for the classic
    // inline editor, which is a dense strip inside an already-labelled card.
    const renderEditorFields = (q, onField, { hideLabelInput = false, autoFocusLabel = false, showLabels = false } = {}) => {
        const isInterested = q.key === "interested";
        const isLibrary = LIBRARY_KEYS.has(q.key);
        const scored = isScored(q);
        const isLocationQ = q.key === LOCATION_QUESTION_KEY;
        const qOptions = parseOptions(q.options);
        const isCTC = CTC_KEYS.includes(q.key);
        // Count the question being edited as present, so a CTC question opened in the modal
        // before it has been added still owns (and can set) the currency.
        const keysWithThis = new Set([...activeKeys, q.key]);
        const ownerKey = CTC_KEYS.find(k => keysWithThis.has(k)) || null;
        const isCurrencyOwner = q.key === ownerKey;
        const isCurrencyReader = isCTC && !isCurrencyOwner && !!ownerKey;

        // Captions the control when showLabels is on, otherwise passes it straight through.
        const wrap = (label, node, opts) => (showLabels ? labelledField(label, node, opts) : node);

        return (
            <>
                {!hideLabelInput && wrap(
                    "Question",
                    <input
                        type="text"
                        value={q.label}
                        onChange={e => onField("label", e.target.value)}
                        // The modal mounts fresh each open, so plain autoFocus is enough there.
                        // The inline (classic) editor persists, hence the once-per-key guard.
                        autoFocus={autoFocusLabel}
                        ref={autoFocusLabel ? undefined : (el => { if (el && !focusedOnce.current.has(q.key)) { focusedOnce.current.add(q.key); setTimeout(() => { try { el.focus(); const l = el.value.length; el.setSelectionRange(l, l); } catch {} }, 50); } })}
                        className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm outline-none focus:border-purple-400 bg-white dark:bg-transparent text-textSecondary dark:text-[#CCCCCC]"
                        placeholder="Question text (e.g. What is your notice period?)"
                    />,
                    { required: true, hint: showLabels ? "This is exactly what the candidate will see." : undefined }
                )}

                {/* Location tag-input — inline city editor for open_to_locations */}
                {isLocationQ && (() => {
                    const cities = qOptions.filter(l => l !== REMOTE_OPTION);
                    return (
                    <div className="flex flex-col gap-1.5 pt-1">
                        <span className={showLabels ? "text-[13px] font-medium text-gray-700 dark:text-gray-300" : "text-[11px] font-medium text-gray-500 dark:text-gray-400"}>Job locations</span>
                        {showLabels && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed -mt-0.5 mb-1">
                                The locations you are hiring for — the candidate picks which of these they
                                are open to. Type a city and press Enter.
                            </p>
                        )}
                        <div className={`flex items-center flex-wrap gap-1.5 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-transparent focus-within:border-purple-400 ${showLabels ? "w-full" : "w-64"}`}>
                            {cities.map(l => (
                                <span key={l} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 text-purple-600 text-xs border border-purple-100 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30 shrink-0">
                                    <MapPin className="w-3 h-3" /> {l}
                                    <button type="button" onClick={() => removeLocation(q, onField, l)} className="hover:text-purple-800 dark:hover:text-purple-200">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                            <input
                                type="text"
                                value={locationInput}
                                onChange={e => setLocationInput(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addLocation(q, onField); } }}
                                // Going straight from typing to "Add question" would otherwise
                                // drop the city and report the question as having none.
                                onBlur={() => addLocation(q, onField)}
                                placeholder={cities.length === 0 ? "Add a city (e.g. New York)" : "Add another city"}
                                className="flex-1 min-w-[80px] text-xs outline-none bg-transparent py-0.5 text-textSecondary dark:text-[#CCCCCC]"
                            />
                            {locationInput.trim() && (
                                <button
                                    type="button"
                                    onClick={() => addLocation(q, onField)}
                                    className="p-1 rounded-md bg-gray-800 text-white hover:bg-gray-900 dark:bg-white/15 dark:hover:bg-white/25 shrink-0"
                                >
                                    <Plus className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </div>
                    );
                })()}

                {/* Currency row — owner shows selector, reader shows read-only label */}
                {isCurrencyOwner && onCurrencyChange && (
                    <div className="flex items-center gap-2">
                        <span className={showLabels ? "text-[13px] font-medium text-gray-700 dark:text-gray-300" : "text-[11px] font-medium text-gray-500 dark:text-gray-400"}>Currency</span>
                        <Dropdown
                            value={currency || ""}
                            onChange={v => onCurrencyChange(v || null)}
                            options={[{ value: "", label: "No currency" }, ...CURRENCIES.map(c => ({ value: c.code, label: c.label }))]}
                            widthClass="w-28"
                        />
                        <span className="text-[13px] text-gray-400 dark:text-gray-500">Annual</span>
                    </div>
                )}
                {isCurrencyReader && (
                    <div className="flex items-center gap-2">
                        <span className={showLabels ? "text-[13px] font-medium text-gray-700 dark:text-gray-300" : "text-[11px] font-medium text-gray-500 dark:text-gray-400"}>Currency</span>
                        <span className="text-xs border border-gray-100 dark:border-gray-700/50 rounded-lg px-2 py-1.5 bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400">
                            {currency ? currencyLabel(currency) : "—"}
                        </span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">Set on Expected CTC</span>
                    </div>
                )}

                {showLabels ? (
                    <>
                        {wrap(
                            "Answer type",
                            <Dropdown
                                value={q.type}
                                onChange={v => onField("type", v)}
                                options={QUESTION_TYPES}
                                disabled={isLibrary}
                                title={isLibrary ? "Library questions have a fixed type" : undefined}
                                widthClass="w-full"
                            />,
                            {
                                hint: isLibrary
                                    ? "Library questions have a fixed answer type."
                                    : "How the candidate answers — free text, a number, Yes/No, and so on.",
                            }
                        )}

                        {(q.type === "select" || q.type === "multiselect") && !isLocationQ && wrap(
                            "Answer options",
                            <OptionsTagInput
                                options={qOptions}
                                onChange={v => onField("options", v)}
                                placeholder="e.g. Remote — press Enter to add"
                            />,
                            { required: true, hint: "The choices the candidate picks from — type one and press Enter." }
                        )}

                        <label className={`flex items-start gap-2 text-[13px] text-gray-600 dark:text-gray-300 ${isInterested ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
                            <input
                                type="checkbox"
                                checked={!!q.required}
                                disabled={isInterested}
                                onChange={e => onField("required", e.target.checked)}
                                className="accent-purple-500 mt-0.5"
                            />
                            <span>
                                Required
                                <span className="block text-[11px] text-gray-400 dark:text-gray-500">
                                    Candidates can&apos;t submit the form without answering this.
                                </span>
                            </span>
                        </label>
                    </>
                ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                        <Dropdown
                            value={q.type}
                            onChange={v => onField("type", v)}
                            options={QUESTION_TYPES}
                            disabled={isLibrary}
                            title={isLibrary ? "Library questions have a fixed type" : undefined}
                            widthClass="w-36"
                        />
                        <label className={`flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ${isInterested ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
                            <input
                                type="checkbox"
                                checked={!!q.required}
                                disabled={isInterested}
                                onChange={e => onField("required", e.target.checked)}
                                className="accent-purple-500"
                            />
                            Required
                        </label>
                        {(q.type === "select" || q.type === "multiselect") && !isLocationQ && (
                            <OptionsTagInput
                                options={qOptions}
                                onChange={v => onField("options", v)}
                                compact
                                placeholder="Add an option — press Enter"
                            />
                        )}
                    </div>
                )}

                {/* Gate row — the answer that qualifies a candidate. Hidden for text and file
                    (never scored), `interested` (intent driver), `current_ctc` (disclosure
                    only), `call_availability` (scheduling info, not a qualifying criterion),
                    and the location question (its gate is the offered cities themselves). */}
                {q.type !== "text" && q.type !== "file" && !isInterested && q.key !== "current_ctc" && q.key !== "call_availability" && !isLocationQ && (() => {
                    const gateTitle = q.type === "multiselect" ? "Accepted answers" : "Qualifying answer";

                    // "at most" / "at least" selector, for the types that compare numerically.
                    const modeControl = (q.type === "number" || q.type === "date") && (
                        MAX_LOCKED_KEYS.has(q.key)
                            ? <span className="text-[11px] text-gray-500 dark:text-gray-400" title="Fixed ceiling — candidates above this are disqualified">at most</span>
                            : (
                                <Dropdown
                                    value={q.match_mode || "exact"}
                                    onChange={v => onField("match_mode", v)}
                                    options={MATCH_MODES}
                                    widthClass="w-24"
                                />
                            )
                    );

                    const valueControl = q.type === "boolean" ? (
                        <Dropdown
                            value={q.ideal_answer === true || q.ideal_answer === "true" ? "true" : q.ideal_answer === false || q.ideal_answer === "false" ? "false" : ""}
                            onChange={v => onField("ideal_answer", v)}
                            options={[{ value: "", label: "No preference" }, { value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                            widthClass="w-32"
                        />
                    ) : q.type === "select" ? (
                        <Dropdown
                            value={q.ideal_answer || ""}
                            onChange={v => onField("ideal_answer", v)}
                            options={[{ value: "", label: "No preference" }, ...qOptions.map(o => ({ value: o, label: o }))]}
                            widthClass="min-w-[140px]"
                        />
                    ) : q.type === "multiselect" ? (
                        qOptions.length === 0 ? (
                            <span className="text-[11px] text-gray-400 dark:text-gray-500">{isLocationQ ? "Add job locations first" : "Add options first"}</span>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {qOptions.map(o => {
                                    const arr = Array.isArray(q.ideal_answer) ? q.ideal_answer : [];
                                    const on = arr.includes(o);
                                    return (
                                        <label key={o} className={`flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300 cursor-pointer border border-gray-200 dark:border-gray-600 py-1 bg-white dark:bg-transparent ${variant === "wizard" ? "rounded-full px-3" : "rounded-lg px-2"}`}>
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                onChange={() => onField("ideal_answer", on ? arr.filter(x => x !== o) : [...arr, o])}
                                                className="accent-purple-500"
                                            />
                                            {o}
                                        </label>
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        <input
                            type={q.type === "number" ? "number" : "date"}
                            min={q.type === "number" ? 0 : undefined}
                            value={q.ideal_answer ?? ""}
                            onChange={e => onField("ideal_answer", e.target.value)}
                            className={`flex-1 min-w-[140px] text-xs border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent text-textSecondary dark:text-[#CCCCCC] dark:[color-scheme:dark] py-1.5 outline-none focus:border-purple-400 ${variant === "wizard" ? "rounded-full px-3" : "rounded-lg px-2"}`}
                            placeholder="Qualifying value"
                        />
                    );

                    const footnotes = (
                        <>
                            {isCTC && numberToWords(q.ideal_answer, currency) && (
                                <p className="w-full text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{numberToWords(q.ideal_answer, currency)}</p>
                            )}
                            {/* In labelled mode the caption above already explains the knock-out. */}
                            {scored && !showLabels && (
                                <p className="w-full text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                                    Candidates whose answer doesn't match are marked not qualified. If the question is optional, a blank answer isn't penalised.
                                </p>
                            )}
                        </>
                    );

                    return (
                        <div className={`pt-2 mt-0.5 border-t border-dashed border-gray-200 dark:border-gray-700/50 ${showLabels ? "flex flex-col gap-1.5" : "flex items-center gap-2 flex-wrap"}`}>
                            {showLabels ? (
                                <>
                                    <span className="text-[13px] font-medium text-gray-700 dark:text-gray-300">
                                        {gateTitle}
                                        <span className="font-normal text-gray-400 dark:text-gray-500"> (optional)</span>
                                    </span>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed mb-1">
                                        Set this to turn the question into a knock-out — candidates whose answer
                                        doesn't match are marked not qualified. Leave it blank to simply collect
                                        the answer.
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {modeControl}
                                        {valueControl}
                                    </div>
                                    {footnotes}
                                </>
                            ) : (
                                <>
                                    <span className="text-[11px] font-medium text-purple-500 dark:text-purple-300" title="Candidates who don't meet this are disqualified">
                                        {gateTitle}
                                    </span>
                                    {modeControl}
                                    {valueControl}
                                    {footnotes}
                                </>
                            )}
                        </div>
                    );
                })()}
            </>
        );
    };

    // Classic-variant inline editor: the shared fields, wrapped in the expanded card body.
    const renderEditor = (q, idx, opts = {}) => (
        <div className="border-t border-gray-100 dark:border-gray-700/50 px-3 py-3 flex flex-col gap-2 bg-gray-50/60 dark:bg-white/[0.03]">
            {renderEditorFields(q, (f, v) => update(idx, f, v), opts)}
        </div>
    );

    // Renders the collapsed summary row for a question card.
    const renderSummaryRow = (q, idx, dragHandleProps = null) => {
        const isInterested = q.key === "interested";
        const scored = isScored(q);
        const isLocationQ = q.key === LOCATION_QUESTION_KEY;
        const qOptions = parseOptions(q.options);
        const hasRemote = isLocationQ && qOptions.some(l => l === REMOTE_OPTION);
        const isCTC = CTC_KEYS.includes(q.key);
        const open = openKeys.has(q.key);
        const summary = scoreSummary(q);

        return (
            <div
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl transition-colors group-hover:bg-purple-50/30 dark:group-hover:bg-white/5 cursor-pointer"
                onClick={() => toggleOpen(q.key)}
            >
                {/* Drag handle — fades in on card hover, only for sortable questions */}
                {dragHandleProps ? (
                    <div
                        {...dragHandleProps}
                        onClick={e => e.stopPropagation()}
                        className="text-gray-300 dark:text-gray-600 hover:text-gray-500 shrink-0 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Drag to reorder"
                    >
                        <GripVertical className="w-4 h-4" />
                    </div>
                ) : (
                    <div className="w-4 shrink-0" />
                )}

                <ChevronDown className={`w-4 h-4 shrink-0 transition-all text-gray-400 group-hover:text-purple-500 ${open ? "" : "-rotate-90"}`} />

                {/* Label — shown collapsed only */}
                {!open && (
                    <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-white group-hover:text-purple-800 dark:group-hover:text-purple-300 truncate transition-colors">
                        {q.label || <span className="text-gray-400 dark:text-gray-500 italic font-normal">Untitled question</span>}
                    </span>
                )}
                {open && <div className="flex-1" />}

                {/* Chips — shown collapsed only */}
                {!open && <Chip tone="type">{TYPE_LABEL[q.type]}</Chip>}
                {!open && isCTC && currency && <Chip tone="muted">{currencyLabel(currency)}</Chip>}
                {!open && isLocationQ && (() => {
                    const cities = qOptions.filter(l => l !== REMOTE_OPTION);
                    return (
                        <>
                            {cities.length > 0 && <Chip tone="muted">{cities.length} {cities.length === 1 ? "city" : "cities"}</Chip>}
                            {hasRemote && <Chip tone="muted">Remote</Chip>}
                        </>
                    );
                })()}
                {isLocationQ ? (
                    qOptions.length > 0 ? null : <Chip tone="muted">Info</Chip>
                ) : scored ? (
                    <Chip tone="score">{summary}</Chip>
                ) : q.required ? (
                    <Chip tone="req">Required</Chip>
                ) : (
                    <Chip tone="muted">Info</Chip>
                )}

                {!isInterested && (
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); removeAt(idx); }}
                        className="text-gray-200 dark:text-gray-600 hover:text-red-400 dark:hover:text-red-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        );
    };

    // Wizard-variant row: a read-only summary of the question. All configuration (type,
    // options, qualifying answer) happens in the modal, reached via the pencil — so the panel
    // stays a scannable list rather than a stack of open editors. `interested` is fixed and
    // non-configurable, so it gets no pencil and keeps its static "Qualifying answer: Yes".
    const renderWizardRow = (q, idx, dragHandleProps = null) => {
        const isInterested = q.key === "interested";
        const scored = isScored(q);
        const summary = scoreSummary(q);
        return (
            <div className="border border-gray-200 dark:border-gray-700/50 rounded-xl bg-white dark:bg-white/5 overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 transition-all">
                {/* Every side element is a 20px-tall flex box (the label's line height) so the
                    grip, pencil and X sit dead-centre on the question text's first line. */}
                <div className="flex items-start gap-2 px-4 py-3">
                    {dragHandleProps ? (
                        <div
                            {...dragHandleProps}
                            className="h-5 flex items-center text-gray-300 dark:text-gray-600 hover:text-gray-500 shrink-0 cursor-grab active:cursor-grabbing"
                            title="Drag to reorder"
                        >
                            <GripVertical className="mt-1 w-4 h-4" />
                        </div>
                    ) : (
                        <div className="w-3.5 h-5 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap min-h-5">
                            <span className="text-base font-normal text-gray-800 dark:text-white">
                                {q.label || <span className="text-gray-400 dark:text-gray-500 italic font-normal">Untitled question</span>}
                            </span>
                            {scored && <Chip tone="gradient" size="md">Knock-Out</Chip>}
                        </div>
                        {/* `interested` is fixed (always "Yes"), so it carries no summary line —
                            the Knock-Out chip already says everything configurable about it. */}
                        {!isInterested && (
                            <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                                {TYPE_LABEL[q.type]}
                                {q.required && " · Required"}
                                {summary && ` · ${summary}`}
                            </p>
                        )}
                    </div>
                    {!isInterested && (
                        <>
                            <button
                                type="button"
                                onClick={() => openEditModal(q, idx)}
                                className="h-5 flex items-center text-gray-300 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                                title="Edit question"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => removeAt(idx)}
                                className="h-5 flex items-center text-gray-300 dark:text-gray-600 hover:text-red-400 dark:hover:text-red-400 shrink-0"
                                title="Remove"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <div className={variant === "wizard" ? "flex gap-4" : "flex flex-1"}>

                {/* ── Library palette (fixed left) ─────────────────────────────── */}
                <div className={variant === "wizard"
                    ? "w-1/3 shrink-0 bg-white dark:glass-effect border border-gray-200 dark:border-gray-700/50 rounded-xl flex flex-col py-1 px-2"
                    : "w-60 shrink-0 bg-white dark:glass-effect border-r border-gray-200 dark:border-gray-700/50 flex flex-col"
                }>
                    <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                        <span className={variant === "wizard" ? "text-base font-medium text-textPrimary dark:text-white" : "text-base font-medium text-gray-700 dark:text-white"}>Question Library</span>
                        <p className={variant === "wizard" ? "text-[11px] text-gray-500 dark:text-gray-400 mt-0.5" : "text-[10px] text-gray-400 dark:text-gray-500 mt-0.5"}>Click to add</p>
                        {showLibrarySearch && (
                            <div className="mt-2 flex items-center gap-1.5 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 focus-within:border-purple-400">
                                <Search className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                                <input
                                    type="text"
                                    value={librarySearch}
                                    onChange={e => setLibrarySearch(e.target.value)}
                                    placeholder="Search the library…"
                                    className={`flex-1 min-w-0 outline-none bg-transparent text-textSecondary dark:text-[#CCCCCC] ${variant === "wizard" ? "text-sm" : "text-xs"}`}
                                />
                            </div>
                        )}
                    </div>
                    <div className={variant === "wizard" ? "p-2 flex flex-col gap-4" : "flex-1 overflow-y-auto p-2 flex flex-col gap-3"}>
                        {filteredLibrary.length === 0 && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center py-4">No questions match your search.</p>
                        )}
                        {filteredLibrary.map(group => (
                            <div key={group.category}>
                                <div className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-textPrimary dark:text-gray-400">
                                    {group.category}
                                </div>
                                <div className="flex flex-col gap-2">
                                    {group.questions.map(q => {
                                        const added = activeKeys.has(q.key);
                                        const locked = q.key === "interested";
                                        return (
                                            <div
                                                key={q.key}
                                                draggable={!added && !locked}
                                                onDragStart={e => {
                                                    e.dataTransfer.setData("libraryKey", q.key);
                                                    e.dataTransfer.effectAllowed = "copy";
                                                }}
                                                onClick={() => {
                                                    if (locked) return;
                                                    // Wizard: adding always goes through the
                                                    // configure modal; clicking an added one
                                                    // takes it back out.
                                                    if (variant !== "wizard") { toggleLibrary(q); return; }
                                                    if (activeKeys.has(q.key)) removeByKey(q.key);
                                                    else openAddModal(q);
                                                }}
                                                style={{ cursor: locked ? "not-allowed" : (added ? "pointer" : "grab") }}
                                                className={`flex items-center gap-4 rounded-lg ${variant === "wizard" ? "px-3 py-2.5 text-sm" : "px-2 py-1.5 text-xs"} border transition-colors select-none ${
                                                    variant === "wizard"
                                                        ? (added
                                                            ? "border-gray-200 dark:border-gray-700/50 bg-white dark:bg-white/10 text-gray-700 dark:text-gray-200 shadow-sm"
                                                            : "border-gray-200 dark:border-gray-700/50 bg-white dark:bg-white/5 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600")
                                                        : added
                                                            ? "border-purple-200 bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30"
                                                            : "border-gray-200 dark:border-gray-700/50 bg-white dark:bg-white/5 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                                                } ${locked && variant !== "wizard" ? "opacity-70" : ""}`}
                                            >
                                                {variant !== "wizard" && (
                                                    <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border ${
                                                        added ? "bg-purple-500 border-purple-500 text-white" : "border-gray-300 dark:border-gray-600 text-transparent"
                                                    }`}>
                                                        <Check className="w-3 h-3" />
                                                    </span>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    {/* Wizard: title + sub-line typography is kept identical to the
                                                        "Your Questions" rows so the two panels read as one list. */}
                                                    <p className={variant === "wizard" ? "text-base font-normal text-textSecondary dark:text-gray-200" : "truncate"}>{variant === "wizard" ? q.label : (q.short || q.label)}</p>
                                                    {/* {variant === "wizard" && (
                                                        <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">Answer Type - {TYPE_LABEL[q.type]}</p>
                                                    )} */}
                                                </div>
                                                {locked && <span className="text-[9px] font-semibold text-textPrimary dark:text-gray-400 shrink-0">Mandatory</span>}
                                                {!added && !locked && variant !== "wizard" && (
                                                    <GripVertical className="w-3 h-3 text-gray-300 dark:text-gray-600 shrink-0" />
                                                )}
                                                {variant === "wizard" && (
                                                    <span
                                                        className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border ${
                                                            added ? "border-transparent text-white shadow-sm" : "border-gray-500 dark:border-gray-500 text-gray-500 dark:text-gray-400"
                                                        }`}
                                                        style={added ? { background: "linear-gradient(to right, #ec4899, #3b82f6)" } : undefined}
                                                    >
                                                        {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Active question set (scrollable right) ───────────────────── */}
                <div className={variant === "wizard"
                    ? "flex-1 min-w-0 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-gray-700/50 rounded-xl flex flex-col py-1 px-2"
                    : "flex-1 min-w-0 bg-gray-50 dark:bg-white/[0.03] flex flex-col overflow-hidden"
                }>
                    {/* The header sits on the page surface rather than the panel's own tint, the
                        same inversion the light theme gets from white-on-gray-50. */}
                    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700/50 shrink-0 bg-white dark:bg-transparent">
                        <div className="flex items-center justify-between">
                            <span className={variant === "wizard" ? "text-base font-medium text-textPrimary dark:text-white" : "text-base font-medium text-gray-700 dark:text-white"}>
                                {variant === "wizard" ? "Your Questions" : "Questions"}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">{questions.length} added</span>
                        </div>
                        {variant === "wizard" && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                Knock-out = candidate is automatically disqualified based on this answer.
                            </p>
                        )}
                    </div>

                    <div
                        className={variant === "wizard" ? "p-3" : "flex-1 overflow-y-auto p-3"}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                            const key = e.dataTransfer.getData("libraryKey");
                            if (!key || activeKeys.has(key)) return;
                            const libQ = LIBRARY_FLAT.find(q => q.key === key);
                            if (!libQ) return;
                            if (variant === "wizard") { openAddModal(libQ); return; }
                            onChange(pinLocked([...questions, toActiveQuestion(libQ)]));
                            openOne(key);
                        }}
                    >
                        {questions.length === 0 && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 py-8 text-center">
                                No questions yet — drag from the library or click to add.
                            </p>
                        )}

                        {/* Interested question — always locked to position 0, outside the Droppable */}
                        {interestedQ && (() => {
                            const idx = questions.indexOf(interestedQ);
                            if (variant === "wizard") {
                                return <div className="mb-2">{renderWizardRow(interestedQ, idx, null)}</div>;
                            }
                            const open = openKeys.has(interestedQ.key);
                            return (
                                <div className="group border border-gray-200 dark:border-gray-700/50 rounded-xl bg-white dark:bg-white/5 overflow-hidden hover:border-purple-200 dark:hover:border-gray-600 hover:shadow-sm transition-all mb-2">
                                    {renderSummaryRow(interestedQ, idx, null)}
                                    {open && renderEditor(interestedQ, idx)}
                                </div>
                            );
                        })()}

                        {resumeQ && (
                            <div className="group border border-gray-200 dark:border-gray-700/50 rounded-xl bg-white dark:bg-white/5 overflow-hidden hover:border-purple-200 dark:hover:border-gray-600 hover:shadow-sm transition-all mb-2">
                                {/* In the wizard the paperclip sits in the drag-handle column (and the
                                    padding matches renderWizardRow), so the title lines up exactly with
                                    every other question label. */}
                                <div className={variant === "wizard" ? "flex items-start gap-2 px-4 py-3" : "flex items-start gap-3 px-3 py-2.5"}>
                                    {variant !== "wizard" && <div className="w-4 shrink-0" />}
                                    {variant === "wizard" ? (
                                        <span className="h-5 flex items-center shrink-0">
                                            <Paperclip className="w-3.5 h-3.5 text-purple-400 mt-1" />
                                        </span>
                                    ) : (
                                        <Paperclip className="w-3.5 h-3.5 text-purple-400 shrink-0 mt-1" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap min-h-5">
                                            <span className="text-base font-normal text-gray-800 dark:text-white">Resume / CV</span>
                                        </div>
                                        <p className={variant === "wizard" ? "text-[12px] text-gray-400 dark:text-gray-500 mt-0.5" : "text-[11px] text-gray-500 dark:text-gray-400 mt-1"}>Candidates attach a PDF, DOC, or DOCX (max 3MB).</p>
                                        {/* <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer mt-2 w-fit">
                                            <input
                                                type="checkbox"
                                                checked={!!resumeQ.required}
                                                onChange={e => update(questions.indexOf(resumeQ), "required", e.target.checked)}
                                                className="accent-purple-500"
                                            />
                                            Required
                                        </label> */}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeByKey("resume_upload")}
                                        className={variant === "wizard"
                                            ? "h-5 flex items-center text-gray-300 dark:text-gray-600 hover:text-red-400 shrink-0"
                                            : "text-gray-200 dark:text-gray-600 hover:text-red-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                        }
                                        title="Remove"
                                    >
                                        {variant === "wizard" ? <X className="w-3.5 h-3.5" /> : <Trash2 className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Sortable questions — inside Droppable for reorder + cross-panel drop */}
                        <Droppable droppableId="questions">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`flex flex-col gap-2 min-h-[4px] rounded-xl transition-colors ${
                                        snapshot.isDraggingOver ? "bg-purple-50/40 dark:bg-white/5" : ""
                                    }`}
                                >
                                    {sortableQuestions.map((q, sortIdx) => {
                                        const idx = questions.indexOf(q);
                                        const open = openKeys.has(q.key);
                                        const draggableId = q.key || `custom_${sortIdx}`;
                                        return (
                                            <Draggable key={draggableId} draggableId={draggableId} index={sortIdx}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={variant === "wizard"
                                                            ? (snapshot.isDragging ? "shadow-lg rounded-xl" : "")
                                                            : `group border rounded-xl bg-white dark:bg-white/5 overflow-hidden transition-all ${
                                                                snapshot.isDragging
                                                                    ? "shadow-lg border-purple-300 dark:border-purple-500/40"
                                                                    : "border-gray-200 dark:border-gray-700/50 hover:border-purple-200 dark:hover:border-gray-600 hover:shadow-sm"
                                                            }`
                                                        }
                                                    >
                                                        {variant === "wizard" ? (
                                                            renderWizardRow(q, idx, provided.dragHandleProps)
                                                        ) : (
                                                            <>
                                                                {renderSummaryRow(q, idx, provided.dragHandleProps)}
                                                                {open && renderEditor(q, idx)}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                        
                        {/* Add custom question — at the bottom of the questions panel */}
                        <button
                            type="button"
                            onClick={variant === "wizard" ? openCustomModal : addCustom}
                            className={variant === "wizard"
                                ? "w-full flex items-center justify-center gap-2 px-2 py-2.5 rounded-full text-[13px] border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400 hover:text-gray-800 dark:hover:text-white dark:hover:bg-white/5 transition-all duration-200 mt-2"
                                : "w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-300 hover:text-purple-600 dark:hover:text-white dark:hover:bg-white/5 mt-2"
                            }
                        >
                            <Pencil className="w-3.5 h-3.5" /> Add custom question
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Configure-question modal (wizard variant) ───────────────────── */}
            {modal && createPortal(
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
                    onMouseDown={e => { if (e.target === e.currentTarget) closeModal(); }}
                >
                    {/* Portaled to <body>, outside the themed page wrapper — so the panel names
                        an opaque dark surface rather than inheriting the page's. */}
                    <div className="bg-bgPrimary dark:bg-[#1B1524] dark:border dark:border-gray-700/50 rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                            <h3 className="text-lg font-medium text-textPrimary dark:text-white">
                                {modal.mode === "edit" ? "Edit question" : "Add question"}
                            </h3>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                title="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-5 py-4 flex flex-col gap-4 overflow-y-auto">
                            {renderEditorFields(modal.draft, patchDraft, { autoFocusLabel: true, showLabels: true })}
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700/50 shrink-0">
                            {/* Only once something has been typed — an untouched "Add question"
                                form opens blank, and greeting it with an error reads as a fault. */}
                            {draftBlocker && modal.draft.label.trim() && (
                                <p className="text-xs text-amber-600 dark:text-amber-300 mr-auto text-left max-w-[60%]">{draftBlocker}</p>
                            )}
                            <button
                                type="button"
                                onClick={closeModal}
                                className="px-5 py-2 text-sm rounded-full border border-gray-200 dark:border-gray-600 text-textSecondary dark:text-[#CCCCCC] hover:bg-gray-50 dark:hover:bg-white/10"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={saveModal}
                                disabled={!draftReady}
                                className={`px-6 py-2 text-sm font-medium text-white rounded-full ${draftReady ? "" : "opacity-50 cursor-not-allowed"}`}
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                {modal.mode === "edit" ? "Save changes" : "Add question"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </DragDropContext>
    );
};

export default QuestionsBuilder;
